import React, { useEffect, useState } from "react";

import { Controller, useFieldArray } from "react-hook-form";
import styled from "@emotion/styled";

// mui component
import FormControlLabel from "../../mui/FormControlLabel";
import FormHelperText from "../../mui/FormHelperText";
import FormControl from "../../mui/FormControl";
import Typography from "../../mui/Typography";
import TextField from "../../mui/TextField_new";
import Checkbox from "../../mui/Checkbox";
import Stack from "../../mui/Stack";
import Box from "../../mui/Box";
import Button from "../../mui/Button";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoComplete from "../../mui/AutoComplete";
import TextField_new from "../../mui/TextField_new";
import { TextareaAutosize } from "@mui/material";

interface Props {
  control: any;
  row?: any;
}
interface WeightageEntry {
  weightage: string;
  measurementCriteria: MeasurementCriteriaEntry[];
}

interface MeasurementCriteriaEntry {
  measurementCriteria: string;
  score: string;
  highestScoreCanBeObtained: string;
  region: string;
  operator: string;
}

const operatorOptions = [
  { id: "<", label: "Less Than" },
  { id: "<=", label: "Less Than or Equal To" },
  { id: ">", label: "Greater Than" },
  { id: ">=", label: "Greater Than or Equal To" },
  { id: "==", label: "Equal To" },
  { id: "!=", label: "Not Equal To" },
  { id: "&&", label: "And" },
  { id: "||", label: "Or" },
];

const AddScoreParameterFrom: React.FC<Props> = (props) => {
  const { control, row } = props;

  const parameters = row?.parameterFormData?.parameters || [];

  const [weightages, setWeightages] = useState<any[]>([
    {
      weightage: "",
      subWeightageParams: [
        {
          subWeightage: "",
          measurementCriteria: [
            {
              measurementCriteria: "",
              score: "",
              highestScoreCanBeObtained: "",
              region: "",
              operator: "",
              metadata: "",
              manageScoreId: "",
            },
          ],
        },
      ],
    },
  ]);

  useEffect(() => {
    if (parameters?.length > 0) {
      setWeightages(parameters);
    } else {
      setWeightages([
        {
          weightage: "",
          subWeightageParams: [
            {
              subWeightage: "",
              measurementCriteria: [
                {
                  measurementCriteria: "",
                  score: "",
                  highestScoreCanBeObtained: "",
                  region: "",
                  operator: "",
                  metadata: "",
                  manageScoreId: "",
                },
              ],
            },
          ],
        },
      ]);
    }
  }, []);

  // Function to add a new weightage
  const addNewWeightage = () => {
    setWeightages([
      ...weightages,
      {
        weightage: "",
        subWeightageParams: [
          {
            subWeightage: "",
            measurementCriteria: [
              {
                measurementCriteria: "",
                score: "",
                highestScoreCanBeObtained: "",
                region: "",
                operator: "",
                metadata: "",
                manageScoreId: "",
              },
            ],
          },
        ],
      },
    ]);
  };

  // Function to add a new sub-parameter under a weightage
  const addNewSubParameter = (weightageIndex: number) => {
    setWeightages((prevWeightages) => {
      const updatedWeightages = [...prevWeightages];
      const newSubParameter = {
        subWeightage: "", // Sub weightage name
        measurementCriteria: [
          {
            measurementCriteria: "", // Measurement criteria
            score: "",
            highestScoreCanBeObtained: "",
            region: "",
            operator: "",
            metadata: "",
            manageScoreId: "",
          },
        ],
      };

      updatedWeightages[weightageIndex].subWeightageParams.push(
        newSubParameter
      );
      return updatedWeightages;
    });
  };

  // Function to add a new measurementCriteria under a specific sub-parameter
  const addNewMeasurementCriteria = (
    weightageIndex: number,
    subParameterIndex: number
  ) => {
    setWeightages((prevWeightages) => {
      const updatedWeightages = [...prevWeightages];
      const newMeasurementCriteria = {
        measurementCriteria: "",
        score: "",
        highestScoreCanBeObtained: "",
        region: "",
        operator: "",
        manageScoreId: "",
        metadata: "{}",
      };

      // Push to the correct `measurementCriteria` array
      updatedWeightages[weightageIndex].subWeightageParams[
        subParameterIndex
      ].measurementCriteria.push(newMeasurementCriteria);

      return updatedWeightages;
    });
  };

  return (
    <Stack>
      <Box sx={{ mb: "10px" }}>
        <Controller
          name="parameterFormData.parameterName"
          control={control}
          // rules={{ required: "Parameter name is required" }}
          render={({ field, fieldState: { error } }) => (
            <>
              <Typography>Parameter name</Typography>
              <FormControl error={!!error} fullWidth>
                <TextField
                  size="small"
                  {...field}
                  onChange={(e: any) => {
                    field.onChange(e);
                  }}
                  sx={{ width: "100%" }}
                  placeholder="Enter parameter name"
                  error={!!error}
                  helperText={error?.message || null}
                />
              </FormControl>
            </>
          )}
        />
      </Box>

      {weightages?.map((entry, weightageIndex) => (
        <React.Fragment key={weightageIndex}>
          <Box
            sx={{
              p: "16px",
              backgroundColor: "#FFF7D1",
              borderRadius: "8px",
              border: "1px solid #997906",
              marginBottom: "16px",
            }}
          >
            <Controller
              name={`parameterFormData.parameters[${weightageIndex}].weightage`}
              control={control}
              // rules={{ required: "Weightage is required" }}
              render={({ field, fieldState: { error } }) => (
                <StyledStack>
                  <Typography>Weightage</Typography>
                  <FormControl error={!!error} fullWidth>
                    <TextField
                      size="small"
                      sx={{ bgcolor: "white" }}
                      {...field}
                      onChange={(e: any) => field.onChange(e.target.value)}
                      placeholder="Enter weightage"
                      error={!!error}
                      helperText={error?.message || null}
                    />
                  </FormControl>
                </StyledStack>
              )}
            />

            {entry.subWeightageParams?.map(
              (subWeitage: any, subWeightIndex: number) => (
                <Box
                  key={subWeightIndex}
                  sx={{
                    backgroundColor: "#fff3c7",
                    padding: "10px",
                    margin: "15px 0px",
                    borderRadius: "8px",
                    border: "1px solid #997906",
                  }}
                >
                  <Box sx={{ marginTop: "3px" }}>
                    <Controller
                      name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].subWeightage`}
                      control={control}
                      // rules={{
                      //   required: "Sub Weightage is required",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <StyledStack>
                          <Typography>Sub weightage</Typography>
                          <FormControl error={!!error} fullWidth>
                            <TextField
                              size="small"
                              sx={{ bgcolor: "white" }}
                              {...field}
                              onChange={(e: any) => {
                                field.onChange(e.target.value);
                              }}
                              placeholder="Enter sub weightage"
                              error={!!error}
                              helperText={error?.message || null}
                            />
                          </FormControl>
                        </StyledStack>
                      )}
                    />
                  </Box>
                  {/* <Box sx={{ marginTop: "3px" }}>
                    <Controller
                      name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].description`}
                      control={control}
                      rules={{
                        required: "Description is required",
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <StyledStack>
                          <Typography>Description</Typography>
                          <FormControl error={!!error} fullWidth>
                            <TextField
                              size="small"
                              sx={{ bgcolor: "white" }}
                              {...field}
                              onChange={(e: any) => {
                                field.onChange(e.target.value);
                              }}
                              placeholder="Enter description"
                              error={!!error}
                              helperText={error?.message || null}
                            />
                          </FormControl>
                        </StyledStack>
                      )}
                    />
                  </Box> */}

                  {subWeitage?.measurementCriteria?.map(
                    (criteria: any, criteriaIndex: number) => (
                      <React.Fragment key={criteriaIndex}>
                        {entry.subWeightageParams.length > 1 &&
                          criteriaIndex > 0 && (
                            <>
                              <Stack
                                direction="row"
                                alignItems={"center"}
                                justifyContent={"center"}
                                gap={1}
                                margin={"10px 0px"}
                              >
                                <Typography
                                  sx={{
                                    whiteSpace: "nowrap",
                                    fontSize: "18px",
                                  }}
                                >
                                  # {criteriaIndex + 1}
                                </Typography>
                                <hr
                                  style={{
                                    margin: "16px 0",
                                    borderColor: "#000",
                                    width: "100%",
                                  }}
                                />
                              </Stack>
                            </>
                          )}
                        <Box
                          sx={{
                            backgroundColor: "#FFF3E0",
                            padding: "10px",
                            borderRadius: "5px",
                            marginTop: "10px",
                            border: "1px solid #997906",
                          }}
                        >
                          {/* Added background color and padding */}
                          <Controller
                            name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].measurementCriteria[${criteriaIndex}].region`}
                            control={control}
                            // rules={{ required: "Region is required" }}
                            render={({ field, fieldState: { error } }) => (
                              <StyledStack>
                                <Typography>Region</Typography>
                                <FormControl error={!!error} fullWidth>
                                  <Stack direction="row" sx={{ mt: "-10px" }}>
                                    {[
                                      "All",
                                      "East",
                                      "West",
                                      "North",
                                      "South",
                                    ].map((btn: any) => {
                                      const isChecked = field.value
                                        ? field.value
                                            .toLowerCase()
                                            .includes(btn.toLowerCase())
                                        : false;
                                      return (
                                        <FormControlLabel
                                          key={btn}
                                          sx={{ fontSize: "10px" }}
                                          control={
                                            <Checkbox
                                              sx={{
                                                fontSize: "10px",
                                                "& .MuiSvgIcon-root": {
                                                  color: "#828282 !important",
                                                },
                                                "&.Mui-checked .MuiSvgIcon-root":
                                                  {
                                                    color: "#FFCA09 !important", // Change color when checked
                                                  },
                                              }}
                                              checked={isChecked}
                                              onChange={() =>
                                                field.onChange(btn)
                                              }
                                            />
                                          }
                                          label={btn}
                                          value={btn}
                                        />
                                      );
                                    })}
                                  </Stack>
                                  <FormHelperText
                                    sx={{
                                      marginTop: "-8px",
                                      marginLeft: "2px",
                                    }}
                                  >
                                    {error ? error.message : " "}
                                  </FormHelperText>
                                </FormControl>
                              </StyledStack>
                            )}
                          />
                          <Box sx={{ marginTop: "3px" }}>
                            <Controller
                              name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].measurementCriteria[${criteriaIndex}].measurementCriteria`}
                              control={control}
                              // rules={{
                              //   required: "Measurement criteria is required",
                              // }}
                              render={({ field, fieldState: { error } }) => (
                                <StyledStack>
                                  <Typography>Measurement Criteria</Typography>
                                  <FormControl error={!!error} fullWidth>
                                    <TextField
                                      size="small"
                                      sx={{ bgcolor: "white" }}
                                      {...field}
                                      onChange={(e: any) => {
                                        field.onChange(e.target.value);
                                      }}
                                      placeholder="Enter measurement criteria"
                                      error={!!error}
                                      helperText={error?.message || null}
                                    />
                                  </FormControl>
                                </StyledStack>
                              )}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "3px",
                              gap: "10px",
                            }}
                          >
                            <Box sx={{ marginTop: "3px" }}>
                              <Controller
                                name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].measurementCriteria[${criteriaIndex}].score`}
                                control={control}
                                // rules={{ required: "Score is required" }}
                                render={({ field, fieldState: { error } }) => (
                                  <StyledStack>
                                    <Typography>Score</Typography>
                                    <FormControl error={!!error} fullWidth>
                                      <TextField
                                        size="small"
                                        sx={{ bgcolor: "white" }}
                                        {...field}
                                        onChange={(e: any) => {
                                          field.onChange(e.target.value);
                                        }}
                                        placeholder="Enter score"
                                        error={!!error}
                                        helperText={error?.message || null}
                                      />
                                    </FormControl>
                                  </StyledStack>
                                )}
                              />
                            </Box>
                            <Box sx={{ marginTop: "3px" }}>
                              <Controller
                                name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].measurementCriteria[${criteriaIndex}].highestScoreCanBeObtained`}
                                control={control}
                                // rules={{
                                //   required: "Percentage is required",
                                // }}
                                render={({ field, fieldState: { error } }) => (
                                  <StyledStack>
                                    <Typography>Percentage</Typography>
                                    <FormControl error={!!error} fullWidth>
                                      <TextField
                                        size="small"
                                        sx={{ bgcolor: "white" }}
                                        {...field}
                                        onChange={(e: any) => {
                                          field.onChange(e.target.value);
                                        }}
                                        placeholder="Enter percentage"
                                        error={!!error}
                                        helperText={error?.message || null}
                                      />
                                    </FormControl>
                                  </StyledStack>
                                )}
                              />
                            </Box>
                          </Box>
                          {/* <Box sx={{ marginTop: "3px" }}>
                  <Controller
                    name={`parameterFormData.parameters[${weightageIndex}].measurementCriteria[${criteriaIndex}].region`}
                    control={control}
                    rules={{
                      required: "region is required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <StyledStack>
                        <Typography>Region</Typography>
                        <FormControl error={!!error} fullWidth>
                          <TextField
                            size="small"
                            sx={{ bgcolor: "white" }}
                            {...field}
                            onChange={(e: any) => {
                              field.onChange(e.target.value);
                            }}
                            placeholder="Enter measurement Region"
                          />
                          <StyledFormHelperText>
                            {error ? error.message : " "}
                          </StyledFormHelperText>
                        </FormControl>
                      </StyledStack>
                    )}
                  />
                </Box> */}
                          <Box sx={{ marginTop: "3px" }}>
                            <Controller
                              name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].measurementCriteria[${criteriaIndex}].operator`}
                              control={control}
                              // rules={{
                              //   required: "operator is required",
                              // }}
                              render={({ field, fieldState: { error } }) => (
                                <StyledStack>
                                  <Typography>Operator</Typography>
                                  <FormControl error={!!error} fullWidth>
                                    <AutoComplete
                                      {...field}
                                      id="operator-select"
                                      options={operatorOptions}
                                      matchKey="id"
                                      labelKey="label"
                                      placeholder="Select operator"
                                      disableClearable={false}
                                      onChange={(value) =>
                                        field.onChange(value)
                                      } // Ensure value is set correctly
                                      // errorMsg={error?.message} // Pass the error message to AutoComplete
                                      loading={false}
                                      sx={{ backgroundColor: "#fff" }}
                                    />
                                    <StyledFormHelperText>
                                      {error ? error.message : " "}
                                    </StyledFormHelperText>
                                  </FormControl>
                                </StyledStack>
                              )}
                            />
                          </Box>

                          <Box sx={{ marginTop: "3px" }}>
                            <Controller
                              name={`parameterFormData.parameters[${weightageIndex}].subWeightageParams[${subWeightIndex}].measurementCriteria[${criteriaIndex}].metadata`}
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <StyledStack>
                                  <Typography>Metadata</Typography>
                                  <FormControl error={!!error} fullWidth>
                                    <TextareaAutosize
                                      {...field}
                                      onChange={(e: any) => {
                                        field.onChange(e.target.value);
                                      }}
                                      value={field.value}
                                      placeholder="Enter metadata"
                                      minRows={3}
                                      style={{
                                        borderRadius: "8px",
                                        width: "100%", // Set width to fill the container
                                        maxWidth: "100%", // Prevent exceeding the container width
                                        padding: "8px", // Optional: Add padding for better UX
                                        fontSize: "14px", // Optional: Match the style with your form
                                        boxSizing: "border-box", // Include padding and border in width calculation
                                        outline: "none", // Optional: Remove the default focus style
                                        border: error
                                          ? "1px solid red"
                                          : "1px solid #8D8D8D",
                                      }}
                                    />
                                  </FormControl>
                                </StyledStack>
                              )}
                            />
                          </Box>

                          <Box
                            sx={{
                              borderBottom:
                                entry?.measurementCriteria?.length > 1 &&
                                criteriaIndex <
                                  entry?.measurementCriteria?.length - 1
                                  ? "1px solid #997906"
                                  : "none",
                              marginBottom:
                                entry?.measurementCriteria?.length > 1 &&
                                criteriaIndex <
                                  entry?.measurementCriteria?.length - 1
                                  ? "16px"
                                  : "none",
                            }}
                          ></Box>
                        </Box>
                      </React.Fragment>
                    )
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      marginTop: "10px",
                      gap: "10px",
                    }}
                  >
                    {/* Flex container for buttons */}
                    <Button
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        border: "1px solid #8D8D8D",
                        backgroundColor: "#FFF3E0",
                        "&:hover": {
                          backgroundColor: "#fff",
                        },
                        borderRadius: "8px",
                        textTransform: "capitalize",
                        boxShadow: "none",
                      }}
                      onClick={() =>
                        addNewMeasurementCriteria(
                          weightageIndex,
                          subWeightIndex
                        )
                      }
                    >
                      Measurement Criteria
                    </Button>
                    <Button
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        border: "1px solid #8D8D8D",
                        backgroundColor: "#FFF3E0",
                        "&:hover": {
                          backgroundColor: "#fff",
                        },
                        borderRadius: "8px",
                        textTransform: "capitalize",
                        boxShadow: "none",
                      }}
                      onClick={() => addNewSubParameter(weightageIndex)}
                    >
                      Sub Weight
                    </Button>
                  </Box>
                </Box>
              )
            )}
          </Box>
        </React.Fragment>
      ))}

      <Box sx={{ marginTop: "10px" }}>
        <Button
          startIcon={<AddRoundedIcon />}
          sx={{
            border: "1px solid #8D8D8D",
            backgroundColor: "#FFF7D1",
            "&:hover": {
              backgroundColor: "#fff",
            },
            borderRadius: "8px",
            textTransform: "capitalize",
            boxShadow: "none",
          }}
          onClick={addNewWeightage}
        >
          New Weightage
        </Button>
      </Box>
    </Stack>
  );
};

const StyledStack = styled(Stack)(() => ({
  marginTop: "0px",
  marginBottom: "2px",
}));

const StyledFormHelperText = styled(FormHelperText)(() => ({
  marginTop: "-2px",
  marginLeft: "2px",
}));

export default AddScoreParameterFrom;
